'use client';

import { useQuery } from '@tanstack/react-query';
import { productAPI } from '@/lib/api';
import ProductCard from './ProductCard';

interface ProductGridProps {
  categoryId?: string;
  categorySlug?: string;
  limit?: number;
  featured?: boolean;
}

export default function ProductGrid({ categoryId, categorySlug, limit = 12, featured }: ProductGridProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['products', { categoryId, categorySlug, limit, featured }],
    queryFn: () => productAPI.getList({
      status: 'published',
      categoryId,
      categorySlug,
      limit,
      featured,
    }),
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(limit)].map((_, i) => (
          <div key={i} className="bg-gray-200 animate-pulse rounded-lg h-64" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">加载产品失败，请稍后重试</p>
      </div>
    );
  }

  if (!data?.data || data.data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">暂无产品</p>
      </div>
    );
  }

  // 检查产品数据完整性
  const productsWithIssues = data.data.filter(p => !p.slug);
  if (productsWithIssues.length > 0) {
    console.warn('[ProductGrid] 发现缺少slug的产品:', productsWithIssues.map(p => ({
      id: p.id,
      title: p.title,
      slug: p.slug,
    })));
  }

  return (
    <>
      <style jsx>{`
        #uc_post_grid_elementor_30c0430.uc_post_grid_style_one .uc_post_grid_style_one_wrap {
          display: grid;
        }
        #uc_post_grid_elementor_30c0430 .uc_post_grid_style_one_item {
          overflow: hidden;
          position: relative;
        }
        #uc_post_grid_elementor_30c0430 .uc_post_image {
          background-position: center;
          transition: 0.3s;
          background-repeat: no-repeat;
          position: relative;
          width: 100%;
          height: 0;
          padding-bottom: 75%; /* 4:3 比例，由网格列宽决定 */
          background-size: cover;
        }
        #uc_post_grid_elementor_30c0430 .uc_post_image_overlay {
          position: absolute;
          top: 0px;
          left: 0px;
          height: 100%;
          width: 100%;
          transition: 0.3s;
        }
        #uc_post_grid_elementor_30c0430 .ue_post_grid_item {
          display: flex;
          flex-direction: column;
        }
        #uc_post_grid_elementor_30c0430.uc_post_grid_style_one .uc_content {
          flex-grow: 1;
        }
        #uc_post_grid_elementor_30c0430.uc_post_grid_style_one .uc_content > div {
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .uc_post_title {
          font-size: 21px;
        }
        .ue-grid-item-category a {
          display: inline-block;
          font-size: 10px;
        }
        #uc_post_grid_elementor_30c0430 .ue-meta-data {
          display: flex;
          flex-wrap: wrap;
          line-height: 1em;
        }
      `}</style>
      <div className="uc_post_grid_style_one" id="uc_post_grid_elementor_30c0430">
        <div className="uc_post_grid_style_one_wrap ue_post_grid uc-items-wrapper">
          {data.data.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </>
  );
}

